import React from 'react';
import PropTypes from 'prop-types';
import { ChevronDownIcon } from '../../icons';
import { borders, surfaces, textColors } from '../../tokens';

/**
 * Select Component
 * 
 * Standardized select dropdown component for forms.
 * Uses native HTML select for maximum compatibility and accessibility.
 * 
 * @example
 * // Basic select
 * <Select
 *   value={country}
 *   onChange={(e) => setCountry(e.target.value)}
 *   options={[
 *     { value: 'us', label: 'United States' },
 *     { value: 'ca', label: 'Canada' },
 *   ]}
 * />
 * 
 * @example
 * // Select with error
 * <Select
 *   value={role}
 *   onChange={(e) => setRole(e.target.value)}
 *   options={roles}
 *   error="Please select a role"
 * />
 */
const Select = ({
  // Value and change handlers
  value = '',
  onChange,
  onBlur,
  onFocus,
  
  // Select attributes
  name,
  id,
  disabled = false,
  required = false,
  autoFocus = false,
  
  // Options
  options = [], // Array of { value, label } or { value, label, disabled }
  placeholder = 'Select an option',
  
  // Sizing
  size = 'md', // 'sm' | 'md' | 'lg'
  fullWidth = true,
  
  // States
  error = '',
  success = '',
  helperText = '',
  
  // Styling
  className = '',
  
  // Accessibility
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
  
  // Additional props
  ...rest
}) => {
  // Determine state
  const hasError = Boolean(error);
  const hasSuccess = Boolean(success) && !hasError;
  const hasHelperText = Boolean(helperText) && !hasError && !hasSuccess;
  
  // Auto-generate IDs for aria-describedby
  const errorId = id ? `${id}-error` : undefined;
  const successId = id ? `${id}-success` : undefined;
  const helperId = id ? `${id}-helper` : undefined;
  
  // Build aria-describedby
  let describedBy = ariaDescribedBy || '';
  if (hasError && errorId) describedBy = describedBy ? `${describedBy} ${errorId}` : errorId;
  if (hasSuccess && successId) describedBy = describedBy ? `${describedBy} ${successId}` : successId;
  if (hasHelperText && helperId) describedBy = describedBy ? `${describedBy} ${helperId}` : helperId;
  
  // Size classes
  const sizeClasses = {
    sm: 'px-3 py-1.5 text-sm pr-8',
    md: 'px-3 py-2 text-base pr-10',
    lg: 'px-4 py-2.5 text-lg pr-12',
  };
  
  // State classes
  const baseClasses = `border rounded-md shadow-sm transition-colors duration-200 focus:outline-none appearance-none bg-white/50 dark:bg-dark-800/50 backdrop-blur-sm ${textColors.primary}`;
  
  let stateClasses = '';
  if (disabled) {
    stateClasses = 'bg-gray-50 dark:bg-dark-800 text-gray-500 dark:text-dark-500 border-gray-300 dark:border-dark-700 cursor-not-allowed';
  } else if (hasError) {
    stateClasses = `border-red-300 dark:border-red-400 focus:ring-2 focus:ring-red-500 dark:focus:ring-red-400 focus:border-red-500 dark:focus:border-red-400`;
  } else if (hasSuccess) {
    stateClasses = `border-green-300 dark:border-green-400 focus:ring-2 focus:ring-green-500 dark:focus:ring-green-400 focus:border-green-500 dark:focus:border-green-400`;
  } else {
    stateClasses = `${borders.default} focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400`;
  }
  
  // Width class
  const widthClass = fullWidth ? 'w-full' : '';
  
  // Combine all classes
  const selectClasses = [
    baseClasses,
    sizeClasses[size],
    stateClasses,
    widthClass,
    className,
  ].filter(Boolean).join(' ');
  
  return (
    <div className={fullWidth ? 'w-full' : 'inline-block'}>
      {/* Select with custom chevron icon */}
      <div className="relative">
        <select
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          onFocus={onFocus}
          name={name}
          id={id}
          disabled={disabled}
          required={required}
          autoFocus={autoFocus}
          className={selectClasses}
          aria-label={ariaLabel}
          aria-describedby={describedBy || undefined}
          aria-invalid={ariaInvalid !== undefined ? ariaInvalid : hasError}
          {...rest}
        >
          {/* Placeholder option */}
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          
          {/* Options */}
          {options.map((option, index) => (
            <option 
              key={option.value || index} 
              value={option.value}
              disabled={option.disabled}
            >
              {option.label}
            </option>
          ))}
        </select>
        
        {/* Chevron Icon */}
        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
          <ChevronDownIcon 
            size="sm" 
            className={
              disabled ? 'text-gray-400 dark:text-dark-500' :
              hasError ? 'text-red-500 dark:text-red-400' : 
              hasSuccess ? 'text-green-500 dark:text-green-400' : 
              'text-gray-400 dark:text-dark-500'
            } 
          />
        </div>
      </div>
      
      {/* Error Message */}
      {hasError && (
        <p id={errorId} className="mt-1 text-xs text-red-600 dark:text-red-400">
          {error}
        </p>
      )}
      
      {/* Success Message */}
      {hasSuccess && (
        <p id={successId} className="mt-1 text-xs text-green-600 dark:text-green-400">
          {success}
        </p>
      )}
      
      {/* Helper Text */}
      {hasHelperText && (
        <p id={helperId} className="mt-1 text-xs text-gray-500 dark:text-dark-500">
          {helperText}
        </p>
      )}
    </div>
  );
};

Select.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  
  name: PropTypes.string,
  id: PropTypes.string,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  autoFocus: PropTypes.bool,
  
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      disabled: PropTypes.bool,
    })
  ),
  placeholder: PropTypes.string,
  
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  fullWidth: PropTypes.bool,
  
  error: PropTypes.string,
  success: PropTypes.string,
  helperText: PropTypes.string,
  
  className: PropTypes.string,
  
  'aria-label': PropTypes.string,
  'aria-describedby': PropTypes.string,
  'aria-invalid': PropTypes.bool,
};

export default Select;
